import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * useSiteShift — resolves "day" vs "night" using the current site's configured
 * shift windows from `/site-config/me`. Matches the mobile app exactly.
 *
 * Site config shape:
 *   shift_times: {
 *     day_start:    "06:30",
 *     day_finish:   "18:30",
 *     night_start:  "18:30",
 *     night_finish: "06:30",
 *     time_zone:    "Australia/Sydney",
 *   }
 *
 * Returns { shift, siteCode, tz, ready }. Falls back to a 06→18 day heuristic
 * if the endpoint is unreachable so the UI never blocks.
 */
let _cache = null; // { fetchedAt, config }

export function useSiteShift() {
  const [state, setState] = useState(() => resolveFromCache());

  useEffect(() => {
    let cancelled = false;
    // Refresh cache if older than 5 min (covers site-switch reloads too)
    if (!_cache || Date.now() - _cache.fetchedAt > 5 * 60 * 1000) {
      (async () => {
        try {
          const { data } = await api.get("/site-config/me");
          _cache = { fetchedAt: Date.now(), config: data };
          if (!cancelled) setState(resolveFromCache());
        } catch {
          // Leave fallback state in place
        }
      })();
    }
    return () => { cancelled = true; };
  }, []);

  return state;
}

function resolveFromCache() {
  const cfg = _cache?.config;
  const st = cfg?.shift_times || {};
  const tz = st.time_zone || undefined;
  const day = { start: parseHHMM(st.day_start, "06:00"), end: parseHHMM(st.day_finish, "18:00") };
  const night = { start: parseHHMM(st.night_start, "18:00"), end: parseHHMM(st.night_finish, "06:00") };
  const now = nowInTZ(tz);
  const shift = classifyShift(now, day, night);
  return {
    shift,
    ready: Boolean(cfg),
    siteCode: cfg?.site_code || null,
    tz,
    dayStart: st.day_start || "06:00",
    dayFinish: st.day_finish || "18:00",
    nightStart: st.night_start || "18:00",
    nightFinish: st.night_finish || "06:00",
  };
}

function parseHHMM(s, fallback) {
  const [h, m] = String(s || fallback).split(":").map((x) => parseInt(x, 10));
  return { h: Number.isFinite(h) ? h : 6, m: Number.isFinite(m) ? m : 0 };
}

/** Minutes since midnight in the given IANA timezone. */
function nowInTZ(tz) {
  const d = new Date();
  if (!tz) return d.getHours() * 60 + d.getMinutes();
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const h = parseInt(parts.find((p) => p.type === "hour")?.value || "0", 10);
    const m = parseInt(parts.find((p) => p.type === "minute")?.value || "0", 10);
    return h * 60 + m;
  } catch {
    return d.getHours() * 60 + d.getMinutes();
  }
}

function classifyShift(nowMin, day, night) {
  const dayStart = day.start.h * 60 + day.start.m;
  const dayEnd = day.end.h * 60 + day.end.m;
  const nightStart = night.start.h * 60 + night.start.m;
  const nightEnd = night.end.h * 60 + night.end.m;

  // Day window: linear [dayStart, dayEnd)
  if (dayStart <= dayEnd) {
    if (nowMin >= dayStart && nowMin < dayEnd) return "day";
  } else {
    if (nowMin >= dayStart || nowMin < dayEnd) return "day";
  }
  // Night window: usually wraps midnight (e.g. 18:30 → 06:30)
  if (nightStart <= nightEnd) {
    if (nowMin >= nightStart && nowMin < nightEnd) return "night";
  } else {
    if (nowMin >= nightStart || nowMin < nightEnd) return "night";
  }
  return "day";
}

export const __clearCache = () => { _cache = null; };
